import { useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  TOURS,
  type TourDef,
  type TourStepDef,
} from '@/lib/constants/onboardingTours';
import { flags } from '@/lib/flags';
import { trackAction } from '@/lib/utils/datadog';
import { setOnboardingTourActive } from '@/lib/utils/onboardingTourActive';
import {
  getTourStep,
  isTourDone,
  markTourDone,
  setTourStep,
  subscribeTour,
} from '@/lib/utils/onboardingTourState';
import { useSessionStore } from '@/store/sessionStore';

/** Marks the anchored control so `globals.css` can lift it above the dimming
 *  overlay and ring it. An attribute, not a class: tailwind-merge can't strip it
 *  and it never collides with the component's own styling. */
const ACTIVE_ATTR = 'data-tour-active';

/** Mirrors the opt-in action names so the funnel reads end to end:
 *  try_now_clicked → tour_started → tour_completed. */
const STARTED = 'v2_onboarding_started';
const COMPLETED = 'v2_onboarding_completed';
const SKIPPED = 'v2_onboarding_skipped';

/** How long to keep looking for a tour's anchors: ~6s, which covers a slow list
 *  query without leaving a timer running behind an idle page. */
const RESOLVE_ATTEMPTS = 20;
const RESOLVE_INTERVAL_MS = 300;

function findTarget(step: TourStepDef): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
}

/**
 * Drives the onboarding tours of the v2 app.
 *
 * One tour at a time, chosen by what is on the page: the first tour in `TOURS`
 * that is unfinished and has anchors present. So the shell is explained on
 * arrival, and the table tour waits until the user opens a page that has a table
 * — there is no route list to keep in step with the app.
 *
 * At most one tour per page visit. Without that, finishing the documents tour
 * would immediately arm the table tour on the same screen, and the user would be
 * walked through thirteen steps in one sitting.
 *
 * Each tour runs once per browser and resumes where it left off: the step is
 * persisted as it advances, and `done` is only written on Finish or Skip.
 *
 * Steps whose control isn't on the page are dropped rather than shown pointing at
 * nothing — `AiProcessingWidget` only renders for a user with a company, and a
 * read-only table has no editable cell — so the step count comes from what was
 * actually found.
 */
export function useOnboardingTour() {
  const user = useSessionStore(s => s.user);
  // A digest of which tours are finished, so finishing one re-runs the resolver
  // below and the next tour can arm without a reload.
  const doneDigest = useSyncExternalStore(
    subscribeTour,
    () => TOURS.map(tour => (isTourDone(tour.id) ? '1' : '0')).join(''),
    () => '', // first paint: nothing resolves, so no tour can flash
  );

  const pathname = useRouterState({
    select: state => state.location.pathname,
  });
  // The page a tour was last finished on, so the next tour waits for the user to
  // go somewhere else rather than starting the moment this one ends.
  const [spentOn, setSpentOn] = useState<string | null>(null);

  const [tour, setTour] = useState<TourDef | null>(null);
  const [steps, setSteps] = useState<TourStepDef[]>([]);
  const [index, setIndex] = useState(0);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const open = steps.length > 0;
  const step = steps[index];
  const isLastStep = open && index === steps.length - 1;

  // Look for anchors repeatedly for a short window, not once.
  //
  // A single frame after mount was enough for the header, which renders with the
  // shell, and wrong for everything else: a page's table, dropzone and rows
  // arrive when their queries resolve, so the first look finds nothing and the
  // tour never ran. Polling also covers a slow network without guessing a single
  // delay that would be too short on one and wasteful on the other.
  useEffect(() => {
    if (!user?.id || open || spentOn === pathname) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function attempt() {
      if (resolve()) return;
      if (++attempts >= RESOLVE_ATTEMPTS) return;
      timer = setTimeout(attempt, RESOLVE_INTERVAL_MS);
    }

    /** Arms the first eligible tour. Returns whether one was found. */
    function resolve(): boolean {
      for (const candidate of TOURS) {
        if (isTourDone(candidate.id)) continue;
        const found = candidate.steps.filter(findTarget);
        if (found.length === 0) continue;

        // Resume by key, falling back to the start when the saved step no longer
        // exists — it may have been renamed, or its control may be absent here.
        const savedKey = getTourStep(candidate.id);
        const resumeAt = Math.max(
          0,
          found.findIndex(s => s.key === savedKey),
        );
        const start = found[resumeAt] as TourStepDef;

        setTourStep(candidate.id, start.key);
        trackAction(STARTED, {
          tour: candidate.id,
          totalSteps: found.length,
          resumedAt: resumeAt > 0 ? resumeAt + 1 : undefined,
          userId: user?.id,
        });
        setTour(candidate);
        setSteps(found);
        setIndex(resumeAt);
        setAnchor(findTarget(start));
        return true;
      }
      return false;
    }

    const frame = requestAnimationFrame(attempt);
    return () => {
      cancelAnimationFrame(frame);
      if (timer) clearTimeout(timer);
    };
  }, [user?.id, open, doneDigest, pathname, spentOn]);

  // Publish whether a step is on screen so lower-priority app-level prompts (the
  // Intercom consent banner) can step aside while the tour holds the stage. Reset
  // on unmount so a mid-tour teardown never leaves the flag stuck on.
  //
  // `flags.onboarding &&`, not `open` alone: the flag is what `OnboardingTour`
  // renders on, so with tours off `open` can still be true — steps resolve
  // normally — while nothing is drawn. Publishing that reported a tour owning a
  // screen it had drawn nothing on, and the consent banner stood aside for it
  // forever, since a tour that renders nothing can never be finished or skipped.
  // The flag answers "does the tour own the screen", so it has to agree with
  // what is actually on it.
  const tourOwnsScreen = flags.onboarding && open;
  useEffect(() => {
    setOnboardingTourActive(tourOwnsScreen);
    return () => setOnboardingTourActive(false);
  }, [tourOwnsScreen]);

  // Rings the anchored control. DOM-only — the anchor itself is set by whichever
  // handler moved the step, so nothing here writes React state. Cleanup runs on
  // every anchor change and on unmount, so a mid-tour navigation cannot leave an
  // element stranded above the overlay.
  useEffect(() => {
    anchor?.setAttribute(ACTIVE_ATTR, 'true');
    anchor?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return () => anchor?.removeAttribute(ACTIVE_ATTR);
  }, [anchor]);

  // If the element the ring is on gets replaced — a re-render swapping the node
  // out — the attribute goes with it and nothing is highlighted. Re-point at the
  // live element. Through rAF so the state write is not synchronous in an effect.
  useEffect(() => {
    if (!step || !anchor || anchor.isConnected) return;
    const frame = requestAnimationFrame(() => setAnchor(findTarget(step)));
    return () => cancelAnimationFrame(frame);
  }, [step, anchor]);

  /**
   * A live anchor for positioning, rather than the captured element.
   *
   * A captured node can be replaced while a step is open — a list finishes
   * loading, a tab row re-renders — and the old one is then detached, with a
   * zero rect. Base UI resolves that to the top-left corner of the viewport,
   * which is where the card was appearing. Reading the DOM at measure time keeps
   * the card on whatever element currently carries the attribute.
   */
  const virtualAnchor = useMemo(
    () =>
      step
        ? {
            getBoundingClientRect: () =>
              findTarget(step)?.getBoundingClientRect() ??
              anchor?.getBoundingClientRect() ??
              new DOMRect(),
            ...(anchor ? { contextElement: anchor } : {}),
          }
        : null,
    [step, anchor],
  );

  function context() {
    return {
      tour: tour?.id,
      step: index + 1,
      totalSteps: steps.length,
      userId: user?.id,
    };
  }

  function close() {
    setTour(null);
    setSteps([]);
    setIndex(0);
    setAnchor(null);
  }

  /** Ends the current tour for good — both exits go through here so neither can
   *  leave it half-finished. */
  function finish() {
    if (tour) markTourDone(tour.id);
    setSpentOn(pathname);
    close();
  }

  /** Moves to a step and re-anchors in the same handler, so the ring and the
   *  popover never disagree about which control the copy is describing. */
  function goTo(nextIndex: number) {
    const target = steps[nextIndex];
    if (!target || !tour) return;
    setTourStep(tour.id, target.key);
    setIndex(nextIndex);
    setAnchor(findTarget(target));
  }

  function next() {
    if (isLastStep) {
      trackAction(COMPLETED, context());
      finish();
      return;
    }
    goTo(index + 1);
  }

  function back() {
    goTo(Math.max(0, index - 1));
  }

  function skip() {
    trackAction(SKIPPED, context());
    finish();
  }

  return {
    open,
    step,
    anchor,
    virtualAnchor,
    stepNumber: index + 1,
    totalSteps: steps.length,
    isFirstStep: index === 0,
    isLastStep,
    next,
    back,
    skip,
  };
}
